/**
 * v-can — one gate for the Add / Edit / Delete controls on every page.
 *
 * THE PROBLEM THIS EXISTS TO STOP.
 *
 * The permissions matrix (85 features x View/Add/Edit/Delete) is enforced in
 * three places that never agreed with each other:
 *
 *   - the menu          hides a nav item on `<slug>__View`
 *   - the router guard  bounces the URL on `<slug>__View`
 *   - the API           `permission:` middleware, real 403s
 *
 * Nothing gated the BUTTONS. A page the user is allowed to open renders its
 * full toolbar, so a View-only role sees "Add Supplier/Trade", clicks it, and
 * either gets a raw 403 (for the handful of features in
 * PERMISSION_ENFORCED_FEATURES) or silently succeeds (for every feature still
 * in shadow mode). Same matrix, same role, two opposite outcomes depending on
 * a server env var the user cannot see — which is what "permissions feel
 * random" actually means.
 *
 * Eighteen files had grown their own hand-rolled version of this check and
 * thirty-two had none. This is the one implementation.
 *
 * USAGE
 *
 *   v-can="'suppliers__Add'"        hide unless the role grants suppliers__Add
 *   v-can="'suppliers'"             action defaults to View
 *   v-can:edit="'suppliers'"        action from the directive argument
 *   v-can="['suppliers__Add', 'suppliers__Edit']"   OR — any one grants
 *   v-can.disable="'suppliers__Add'"  grey the control out instead of hiding it
 *   v-can.readonly="['jobs__Add', 'jobs__Edit']"   make a whole FORM read-only
 *
 * `.readonly` is for the fields rather than the controls, and it goes on the
 * CONTAINER -- a dialog, a card, a <form> -- not on each input. Hiding the Save
 * button stops the write but leaves a form that still looks editable: a
 * view-only user types into it, finds no way to save, and reasonably reports
 * that saving is broken. This makes the whole form state what it is.
 *
 * It is NOT `.disable`. Disabling greys a field to half opacity and drops it
 * from the tab order, which is wrong for a form whose entire purpose is still
 * to be READ. Text inputs get the native `readonly` attribute: full contrast,
 * still focusable, still selectable and copyable, just not editable.
 *
 * Buttons, links and tabs inside the container stay live -- Cancel must still
 * close, and tabs must still switch -- so only form controls are neutralised.
 * The controls `readonly` does not cover (checkboxes, radios, selects, file
 * inputs, Vuetify's select/autocomplete fields and the date pickers) are
 * blocked by one capture-phase listener on the container instead.
 *
 * Fields that appear LATER are covered too: dialogs mount their body lazily and
 * rows are added after the fact, so a MutationObserver re-runs the sweep on
 * anything added while the container is read-only.
 *
 * The permission KEY must be a catalogue slug from bsapi
 * config/permission_catalogue.php. Route names are the same slugs (the router
 * guard and the nav build `<route name>__View`), so `suppliers`, `customers`,
 * `jobs` etc. are all valid here.
 *
 * NOT A SECURITY BOUNDARY. This hides UI the user may not use; the server is
 * what refuses the request. Hiding a button whose endpoint is still in shadow
 * mode makes the UI honest, it does not make the endpoint safe — that needs the
 * feature added to PERMISSION_ENFORCED_FEATURES.
 *
 * FAILS CLOSED, deliberately, matching utils/permissions.hasPermission(): a key
 * the role does not mention is denied. A control that flashes on and then
 * 403s is worse than one that never appears.
 *
 * WHY display:none AND NOT REMOVING THE NODE. Vue patches this element from its
 * vnode on every re-render. Detaching it from the DOM here desynchronises the
 * vdom and the next patch throws or resurrects the element. Toggling inline
 * display is patch-safe and re-evaluated in `updated`, so the gate follows a
 * role change without a reload.
 */

import type { Directive, DirectiveBinding, VNode } from 'vue';
import { store } from '@/storeVuex';
import { isFullAdmin } from '@/utils/fullAdmin';
import { hasPermission } from '@/utils/permissions';

/** Remembers each element's own inline display so hiding is reversible. */
const PRIOR_DISPLAY = new WeakMap<HTMLElement, string>();
/** The capture-phase blocker installed by `.disable`, so it can be removed. */
const BLOCKERS = new WeakMap<HTMLElement, EventListener>();

const CLICKISH = ['click', 'mousedown', 'mouseup', 'keydown', 'touchstart'];

/**
 * "suppliers__Add" -> ['suppliers', 'Add'];  "suppliers" -> ['suppliers', arg].
 *
 * The argument form (v-can:edit) is lowercase in the template but the stored
 * suffix is capitalised (role_settings holds "suppliers__Edit"), so normalise
 * rather than trusting the author's casing.
 */
function splitKey(raw: string, arg?: string): [string, string] {
    const value = String(raw ?? '').trim();
    if (!value) return ['', 'View'];

    if (value.includes('__')) {
        const [slug, action] = value.split('__');
        return [slug, capitalise(action)];
    }

    return [value, capitalise(arg || 'View')];
}

function capitalise(action: string): string {
    const a = String(action ?? '').trim();
    if (!a) return 'View';
    // "view_all" -> "View_All": each underscore-separated part is capitalised,
    // because the Team Tracker keys are stored that way.
    return a
        .split('_')
        .map((p) => (p ? p.charAt(0).toUpperCase() + p.slice(1).toLowerCase() : p))
        .join('_');
}

/** Does the signed-in user hold any one of the bound keys? */
function granted(binding: DirectiveBinding): boolean {
    const user = (store as any)?.getters?.isLoggedIn?.user;

    // Full admins bypass the matrix entirely — same rule the router guard and
    // the server (User::isInAppAdmin) apply.
    if (isFullAdmin(user)) return true;

    const raw = binding.value;
    const keys = Array.isArray(raw) ? raw : [raw];

    // No key given is an authoring mistake, not a grant. Say so loudly in dev
    // and deny, rather than quietly showing the control to everyone.
    const usable = keys.filter((k) => String(k ?? '').trim() !== '');
    if (usable.length === 0) {
        if (import.meta.env.DEV) {
            console.warn('[v-can] no permission key given; the element will be hidden');
        }
        return false;
    }

    return usable.some((k) => {
        const [slug, action] = splitKey(String(k), binding.arg);
        return slug !== '' && hasPermission(user, slug, action);
    });
}

/**
 * The same rule as a value, for a script that has to word something by it
 * rather than hide a control (e.g. not inviting a user to save what they may
 * not save).
 */
export function userCan(key: string): boolean {
    return granted({ value: key } as DirectiveBinding);
}

function hide(el: HTMLElement): void {
    if (!PRIOR_DISPLAY.has(el)) {
        PRIOR_DISPLAY.set(el, el.style.display);
    }
    el.style.display = 'none';
    el.setAttribute('aria-hidden', 'true');
}

function show(el: HTMLElement): void {
    if (PRIOR_DISPLAY.has(el)) {
        el.style.display = PRIOR_DISPLAY.get(el) as string;
        PRIOR_DISPLAY.delete(el);
    }
    el.removeAttribute('aria-hidden');
}

/**
 * The value `.disable` writes to `disabled`. A page's own `:disabled` binding
 * writes "" (Vue sets the `disabled` DOM property, which reflects as ""), so the
 * value says whose the attribute currently is.
 */
const OUR_DISABLED = 'v-can';

/**
 * What the element had before `.disable` touched it, so enabling hands back the
 * page's own state rather than clearing it.
 */
interface PriorState {
    disabled: string | null;
    ariaDisabled: string | null;
    pointerEvents: string;
    opacity: string;
    cursor: string;
}
const PRIOR_STATE = new WeakMap<HTMLElement, PriorState>();

/** What `.disable` writes. */
const WRITTEN: PriorState = {
    disabled: OUR_DISABLED,
    ariaDisabled: 'true',
    pointerEvents: 'none',
    opacity: '0.5',
    cursor: 'not-allowed'
};

function readState(el: HTMLElement): PriorState {
    return {
        disabled: el.getAttribute('disabled'),
        ariaDisabled: el.getAttribute('aria-disabled'),
        pointerEvents: el.style.pointerEvents,
        opacity: el.style.opacity,
        cursor: el.style.cursor
    };
}

function writeState(el: HTMLElement, state: PriorState): void {
    restoreAttribute(el, 'disabled', state.disabled);
    restoreAttribute(el, 'aria-disabled', state.ariaDisabled);
    el.style.pointerEvents = state.pointerEvents;
    el.style.opacity = state.opacity;
    el.style.cursor = state.cursor;
}

/**
 * The page's own value for each field: what is on the element now, unless that
 * is still what `.disable` wrote, in which case the last value the page had.
 *
 * Anything but our value was written by the page since our last pass (Vue
 * patched a binding: cleared it, or set its own), so it stands. A page that
 * writes exactly our value cannot be told apart from us; for `disabled` the
 * 'v-can' sentinel rules that out, for the rest it is the one case left.
 *
 * `aria-disabled` has no sentinel ('true' is the only value a page writes, and
 * an invalid one reads as false), so a page that set it to 'true' while v-can
 * held the control would read as ours and lose it on release. Where the page
 * binds it, the binding is read instead (`bound`), which settles it exactly.
 * Only a component that sets it on its own root without a binding is still
 * inferred from the DOM; none in src/ does today.
 */
function pagesOwn(prior: PriorState, current: PriorState, bound: string | null | undefined): PriorState {
    const pick = <K extends keyof PriorState>(key: K): PriorState[K] =>
        current[key] === WRITTEN[key] ? prior[key] : current[key];
    return {
        disabled: pick('disabled'),
        ariaDisabled: bound === undefined ? pick('ariaDisabled') : bound,
        pointerEvents: pick('pointerEvents'),
        opacity: pick('opacity'),
        cursor: pick('cursor')
    };
}

/**
 * The page's own `aria-disabled`, from its `:aria-disabled` binding on this
 * element: undefined when it binds none. Vue removes the attribute for null or
 * undefined and writes any other value as a string, so this is what Vue would
 * have on the element were v-can not holding it.
 */
function boundAriaDisabled(vnode?: VNode): string | null | undefined {
    const props = vnode?.props;
    if (!props) return undefined;
    for (const key of ['aria-disabled', 'ariaDisabled']) {
        if (Object.prototype.hasOwnProperty.call(props, key)) {
            const value = props[key];
            return value == null ? null : String(value);
        }
    }
    return undefined;
}

/**
 * `.disable` keeps the control visible but inert.
 *
 * Three things are needed and none is sufficient alone: the `disabled`
 * attribute (native buttons and inputs), pointer-events (the <div> icon
 * buttons the tables use, which ignore `disabled`), and a capture-phase
 * listener — because a Vue @click bound on the element itself still fires even
 * when pointer-events is none if the event is dispatched programmatically.
 */
function disable(el: HTMLElement, vnode?: VNode): void {
    // Re-read on every pass, so a change the page makes while v-can holds the
    // control is what enable() hands back, not the state at the first disable.
    const current = readState(el);
    const prior = PRIOR_STATE.get(el);
    PRIOR_STATE.set(el, pagesOwn(prior ?? current, current, boundAriaDisabled(vnode)));

    writeState(el, WRITTEN);
    el.classList.add('v-can-disabled');

    if (BLOCKERS.has(el)) return;
    const blocker: EventListener = (event) => {
        event.stopImmediatePropagation();
        event.preventDefault();
    };
    BLOCKERS.set(el, blocker);
    CLICKISH.forEach((type) => el.addEventListener(type, blocker, true));
}

/**
 * Undo disable(), and only that. Every gate calls this on every pass, so it
 * must leave alone a control the PAGE disabled, such as a Save button bound to
 * `:disabled="isSubmitting"` or a paid button under the ledger lock. Clearing
 * `disabled` unconditionally (smoke S1) put those back in the tab order and had
 * a screen reader announce them as active. A Vuetify button still ignored the
 * click, but a plain <button> or <input> became usable again.
 */
function enable(el: HTMLElement, vnode?: VNode): void {
    const prior = PRIOR_STATE.get(el);
    if (prior) {
        // Whatever the page has written since our last pass stands; only our own
        // values are handed back. So `aria-disabled` can't be put back on a
        // control the page has since enabled.
        writeState(el, pagesOwn(prior, readState(el), boundAriaDisabled(vnode)));
        PRIOR_STATE.delete(el);
    }
    el.classList.remove('v-can-disabled');

    const blocker = BLOCKERS.get(el);
    if (blocker) {
        CLICKISH.forEach((type) => el.removeEventListener(type, blocker, true));
        BLOCKERS.delete(el);
    }
}

function restoreAttribute(el: HTMLElement, name: string, value: string | null): void {
    if (value === null) el.removeAttribute(name);
    else el.setAttribute(name, value);
}

/* ------------------------------------------------------------------------- *
 * .readonly — a whole form, read but not written
 * ------------------------------------------------------------------------- */

/** Observers and blockers installed by `.readonly`, so they can be removed. */
const OBSERVERS = new WeakMap<HTMLElement, MutationObserver>();
const RO_BLOCKERS = new WeakMap<HTMLElement, EventListener>();

/** Elements this directive set `readonly` on, so it only clears its own. */
const RO_OWNED = new WeakMap<HTMLElement, Set<Element>>();

/**
 * Controls the native `readonly` attribute does nothing for.
 *
 * `readonly` is specified for text-entry controls only. A checkbox, radio,
 * file, range or <select> ignores it completely, and Vuetify's select,
 * autocomplete and date pickers open their menu from the FIELD wrapper rather
 * than the inner input — which is already `readonly` by design. All of these
 * need the event blocker instead.
 */
const OBSERVE_OPTS: MutationObserverInit = {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['readonly']
};

const UNPROTECTED = [
    'input[type="checkbox"]',
    'input[type="radio"]',
    'input[type="file"]',
    'input[type="range"]',
    'input[type="color"]',
    'select',
    '.v-selection-control',
    '.v-select .v-field',
    '.v-autocomplete .v-field',
    '.v-combobox .v-field',
    '.v-slider',
    '.dp__main'
].join(',');

/** Things that must keep working inside a read-only form. */
const STAYS_LIVE = 'button,a,[role="tab"],[role="button"],.v-btn,.v-tab,.v-expansion-panel-title';

/** Text-entry controls, where the native attribute is the right answer. */
function textControls(root: HTMLElement): Element[] {
    return Array.from(root.querySelectorAll('input,textarea')).filter((el) => {
        const type = (el.getAttribute('type') || 'text').toLowerCase();
        return el.tagName === 'TEXTAREA' || !['checkbox', 'radio', 'file', 'range', 'color', 'button', 'submit'].includes(type);
    });
}

function markReadonly(root: HTMLElement): void {
    const owned = RO_OWNED.get(root) ?? new Set<Element>();

    for (const el of textControls(root)) {
        // Never claim a field the page itself marked readonly, or restoring
        // would hand edit access to something that was never editable.
        if (el.hasAttribute('readonly')) {
            if (!owned.has(el)) continue;
        } else {
            el.setAttribute('readonly', 'readonly');
            owned.add(el);
        }
        el.setAttribute('aria-readonly', 'true');
    }

    RO_OWNED.set(root, owned);
    root.classList.add('v-can-readonly');
}

function clearReadonly(root: HTMLElement): void {
    const owned = RO_OWNED.get(root);
    if (owned) {
        for (const el of owned) {
            el.removeAttribute('readonly');
            el.removeAttribute('aria-readonly');
        }
        RO_OWNED.delete(root);
    }
    root.classList.remove('v-can-readonly');
}

/**
 * Swallow interactions aimed at a control `readonly` cannot protect.
 *
 * Capture phase on the CONTAINER, because Vuetify binds its own handlers on the
 * field wrapper and a bubbling listener would run after the menu had opened.
 */
function readonlyBlocker(): EventListener {
    return (event: Event) => {
        const target = event.target as HTMLElement | null;
        if (!target?.closest) return;

        // Cancel still closes, tabs still switch, panels still expand.
        if (target.closest(STAYS_LIVE)) return;

        // A readonly text field keeps focus, selection and copy.
        if (!target.closest(UNPROTECTED)) return;

        event.stopImmediatePropagation();
        event.preventDefault();
    };
}

function enterReadonly(el: HTMLElement): void {
    markReadonly(el);

    if (!RO_BLOCKERS.has(el)) {
        const blocker = readonlyBlocker();
        RO_BLOCKERS.set(el, blocker);
        CLICKISH.forEach((type) => el.addEventListener(type, blocker, true));
    }

    // Two ways a field escapes the sweep, both of which happen in practice:
    //
    //  - it mounts LATER. Dialog bodies and tab panes render lazily and lists
    //    grow, so a field that did not exist at the first pass comes up
    //    editable.
    //  - it loses the attribute. `readonly` is set on the DOM node, not through
    //    the vnode, so a component that re-renders with its own `:readonly`
    //    binding can patch ours away underneath us.
    //
    // The observer therefore watches childList AND the readonly attribute, and
    // disconnects around its own writes — re-marking inside the callback would
    // otherwise queue another record and spin.
    if (!OBSERVERS.has(el) && typeof MutationObserver !== 'undefined') {
        const observer = new MutationObserver(() => {
            observer.disconnect();
            markReadonly(el);
            observer.observe(el, OBSERVE_OPTS);
        });
        observer.observe(el, OBSERVE_OPTS);
        OBSERVERS.set(el, observer);
    }
}

function exitReadonly(el: HTMLElement): void {
    OBSERVERS.get(el)?.disconnect();
    OBSERVERS.delete(el);

    const blocker = RO_BLOCKERS.get(el);
    if (blocker) {
        CLICKISH.forEach((type) => el.removeEventListener(type, blocker, true));
        RO_BLOCKERS.delete(el);
    }

    clearReadonly(el);
}

function apply(el: HTMLElement, binding: DirectiveBinding, vnode?: VNode): void {
    const allowed = granted(binding);

    if (binding.modifiers.readonly) {
        // A read-only form is still shown and its buttons are still governed by
        // their own v-can, so neither hide() nor disable() applies here.
        show(el);
        enable(el, vnode);
        allowed ? exitReadonly(el) : enterReadonly(el);
        return;
    }

    if (binding.modifiers.disable) {
        // Never leave an element both hidden and disabled if the modifier was
        // added after a first pass hid it.
        show(el);
        allowed ? enable(el, vnode) : disable(el, vnode);
        return;
    }

    enable(el, vnode);
    allowed ? show(el) : hide(el);
}

export const can: Directive<HTMLElement, string | string[]> = {
    mounted: apply,
    updated: apply,
    unmounted(el) {
        exitReadonly(el);
        PRIOR_DISPLAY.delete(el);
        PRIOR_STATE.delete(el);
        const blocker = BLOCKERS.get(el);
        if (blocker) {
            CLICKISH.forEach((type) => el.removeEventListener(type, blocker, true));
            BLOCKERS.delete(el);
        }
    }
};

export default can;
